import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Smartphone, Battery, Monitor, Wifi, HardDrive, Cpu,
  ChevronDown, ChevronRight, RefreshCw, Thermometer,
  Zap, Signal, Globe, Instagram, Mail, Clock, Hash,
  Server, Layers, Activity, Package, ShieldOff, ShieldCheck,
  Download, CheckCircle, XCircle, Loader, Pencil, X, Save, RotateCcw, ExternalLink,
  Video
} from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`
  padding: 10px 0;
  width: 100%;
`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 22px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 10px;
  span { font-size: 13px; font-weight: 500; color: var(--text-muted); }
`;

const RefreshBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 16px;
  border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg);
  color: var(--text-secondary); font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all .2s;
  &:hover { border-color: var(--primary); color: var(--primary); }
  &:disabled { opacity: .5; cursor: not-allowed; }
  svg { transition: transform .3s; }
  &.spinning svg { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

const DeviceBanner = styled.div`
  display: flex; align-items: center; gap: 16px; padding: 20px;
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 14px; margin-bottom: 20px;
`;

const DeviceIcon = styled.div`
  width: 56px; height: 56px; border-radius: 14px; background: var(--gradient);
  display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0;
`;

const DeviceMeta = styled.div`
  flex: 1;
  h2 { font-size: 16px; font-weight: 700; color: var(--text-primary); margin-bottom: 2px; }
`;

const MetaRow = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 4px;
`;

const MetaTag = styled.div`
  display: flex; align-items: center; gap: 4px; font-size: 11px;
  color: var(--text-muted); font-weight: 500;
  svg { opacity: .6; }
`;

const StatusBadge = styled.span`
  font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 20px;
  background: ${p => p.$online ? 'rgba(124,200,142,.15)' : 'rgba(239,68,68,.1)'};
  color: ${p => p.$online ? 'var(--success)' : 'var(--error)'};
`;

const CardsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 14px; overflow: hidden; transition: all .2s;
  &:hover { box-shadow: var(--shadow-hover); }
`;

const CardHead = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 14px 18px; background: none; border: none; cursor: pointer;
  color: var(--text-primary); font-size: 14px; font-weight: 700;
  transition: background .15s;
  &:hover { background: var(--surface-hover); }
  svg:first-child { color: var(--primary); }
  svg:last-child { margin-left: auto; color: var(--text-muted); }
`;

const CardBody = styled.div`
  max-height: ${p => p.$open ? '600px' : '0'};
  overflow: hidden; transition: max-height .3s ease;
`;

const CardContent = styled.div`
  padding: 0 18px 16px;
`;

const InfoRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0; border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

const InfoLabel = styled.div`
  font-size: 12px; color: var(--text-muted); font-weight: 500;
  display: flex; align-items: center; gap: 6px;
`;

const InfoValue = styled.div`
  font-size: 13px; font-weight: 600; color: var(--text-primary);
  font-family: ${p => p.$mono ? "'JetBrains Mono', monospace" : 'inherit'};
`;

const ProgressBar = styled.div`
  width: 100%; height: 6px; border-radius: 3px; background: var(--surface);
  margin-top: 6px; overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%; border-radius: 3px; transition: width .5s ease;
  width: ${p => p.$pct}%;
  background: ${p => p.$pct > 80 ? 'var(--error)' : p.$pct > 50 ? 'var(--warning)' : 'var(--success)'};
`;

const NoDevice = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 400px; color: var(--text-muted);
  svg { opacity: .3; margin-bottom: 16px; }
  h3 { font-size: 16px; margin-bottom: 6px; color: var(--text-secondary); }
  p { font-size: 13px; }
`;

const ActionBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 14px;
  border-radius: 8px; font-size: 11px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => p.$danger ? 'var(--error)' : p.$success ? 'var(--success)' : 'var(--border)'};
  background: ${p => p.$danger ? 'rgba(239,68,68,.1)' : p.$success ? 'rgba(34,197,94,.1)' : 'var(--surface)'};
  color: ${p => p.$danger ? 'var(--error)' : p.$success ? 'var(--success)' : 'var(--text-secondary)'};
  transition: all .2s;
  &:hover { opacity: .85; transform: translateY(-1px); }
  &:disabled { opacity: .4; cursor: not-allowed; transform: none; }
`;

const ApkItem = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; border-radius: 8px; background: var(--surface);
  border: 1px solid var(--border); margin-bottom: 8px;
  transition: all .2s;
  &:hover { border-color: var(--primary); }
`;

const ApkName = styled.div`
  font-size: 12px; font-weight: 600; color: var(--text-primary);
  span { font-size: 10px; color: var(--text-muted); font-weight: 400; margin-left: 6px; }
`;

const StatusDot = styled.span`
  display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600;
  color: ${p => p.$ok ? 'var(--success)' : 'var(--text-muted)'};
`;

const EditBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 16px;
  border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg);
  color: var(--text-secondary); font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all .2s;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(0,0,0,.5); display: flex;
  align-items: center; justify-content: center;
`;

const ModalCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 16px; padding: 28px; width: 560px; max-width: 95vw;
  max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,.2);
`;

const ModalTitle = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;
  h3 { font-size: 16px; font-weight: 700; color: var(--text-primary); margin: 0;
    display: flex; align-items: center; gap: 10px; }
`;

const MGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px;
`;

const MField = styled.div`
  &.wide { grid-column: 1 / -1; }
  label { display: block; font-size: 10px; font-weight: 600; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: .3px; margin-bottom: 4px; }
  input, select { width: 100%; padding: 8px 12px; border-radius: 8px;
    border: 1px solid var(--border); background: var(--surface);
    color: var(--text-primary); font-size: 13px;
    &:focus { border-color: var(--primary); outline: none; } }
`;

const MActions = styled.div`
  display: flex; gap: 10px; margin-top: 20px;
`;

const MBtn = styled.button`
  flex: ${p => p.$primary ? 1 : 'none'}; padding: 12px 20px;
  border-radius: 10px; font-weight: 700; font-size: 13px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  border: ${p => p.$primary ? 'none' : '1px solid var(--card-border)'};
  background: ${p => p.$primary ? 'var(--gradient)' : 'transparent'};
  color: ${p => p.$primary ? '#fff' : 'var(--text-muted)'};
  transition: all .15s;
  &:hover { transform: translateY(-1px); }
`;

const RoleToggle = styled.button`
  flex: 1; padding: 10px 16px; border-radius: 8px; font-weight: 700; font-size: 12px;
  cursor: pointer; transition: all .15s; display: flex; align-items: center;
  justify-content: center; gap: 8px;
  border: 2px solid ${p => p.$active ? (p.$warn ? 'var(--warning)' : 'var(--primary)') : 'var(--card-border)'};
  background: ${p => p.$active ? (p.$warn ? 'rgba(232,184,74,.1)' : 'rgba(165,145,239,.1)') : 'transparent'};
  color: ${p => p.$active ? (p.$warn ? 'var(--warning)' : 'var(--primary)') : 'var(--text-muted)'};
`;

/* ═══════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════ */

const fmtKB = (kb) => {
  if (!kb || kb === 0) return 'N/A';
  if (kb > 1048576) return `${(kb / 1048576).toFixed(1)} GB`;
  if (kb > 1024) return `${(kb / 1024).toFixed(0)} MB`;
  return `${kb} KB`;
};

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const KNOWN_APPS = [
  { name: 'Instagram', pkg: 'com.instagram.android', color: '#E1306C',
    icon: 'instagram', mirror: 'https://www.apkmirror.com/apk/instagram/instagram-instagram/',
    apkKeywords: ['instagram', 'com.instagram.android'] },
  { name: 'Qlickr Keyboard', pkg: 'com.qlickr.keyboard', color: 'var(--primary)',
    icon: 'keyboard', mirror: null,
    apkKeywords: ['qlickrkeyboard', 'qlickr-keyboard', 'com.qlickr.keyboard'] },
  { name: 'TikTok', pkg: 'com.zhiliaoapp.musically', color: '#000',
    icon: 'tiktok', mirror: 'https://www.apkmirror.com/apk/tiktok-pte-ltd/tik-tok/',
    apkKeywords: ['tiktok', 'musically'] },
  { name: 'Gmail', pkg: 'com.google.android.gm', color: '#D93025',
    icon: 'mail', mirror: 'https://www.apkmirror.com/apk/google-inc/gmail/',
    apkKeywords: ['gmail', 'com.google.android.gm'] },
];

const APP_ICONS = {
  instagram: (props) => <Instagram {...props} />,
  keyboard: (props, isDark) => (
    <img
      src={isDark ? '/gifs/Qlickr-Logo-Rond-Blanc-Fond-Noir.gif' : '/gifs/Qlickr-Logo-Rond-Violet-Fond-Blanc.gif'}
      alt="Qlickr" style={{ width: props.size || 18, height: props.size || 18, borderRadius: '50%', objectFit: 'cover' }}
    />
  ),
  tiktok: (props) => <Video {...props} />,
  mail: (props) => <Mail {...props} />,
};

const ApkVersionSelect = styled.select`
  padding: 5px 8px; border-radius: 6px; font-size: 11px; font-weight: 600;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); cursor: pointer;
  &:focus { border-color: var(--primary); outline: none; }
`;

const MirrorLink = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px; font-weight: 600; color: var(--primary);
  text-decoration: none; opacity: .8; transition: opacity .15s; cursor: pointer;
  &:hover { opacity: 1; text-decoration: underline; }
`;

const openExternal = (url) => {
  try {
    const { shell } = window.require('electron');
    shell.openExternal(url);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const DeviceInfo = ({ selectedDevice }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.isDark !== false;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCards, setOpenCards] = useState({
    system: true, battery: true, screen: true,
    network: true, storage: true, cpu: true, apps: true
  });
  const [apks, setApks] = useState([]);
  const [appVersions, setAppVersions] = useState({});
  const [freezing, setFreezing] = useState(false);
  const [installing, setInstalling] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [allAccounts, setAllAccounts] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [igClones, setIgClones] = useState([]);
  const [selectedApkByPkg, setSelectedApkByPkg] = useState({});

  const deviceId = selectedDevice?.device_id;

  // Load instagram accounts for the account selector in edit modal
  useEffect(() => {
    axios.get('/api/instagram-accounts').then(r => {
      if (r.data.success) setAllAccounts(r.data.accounts || []);
    }).catch(() => {});
  }, []);

  const openEditModal = () => {
    const dev = info?.device || {};
    setEditForm({
      device_name: selectedDevice?.device_name || dev.device_name || '',
      android_version: dev.android_version || selectedDevice?.android_version || '',
      gmail_account: dev.gmail_account || selectedDevice?.gmail_account || '',
      instagram_version: dev.instagram_version || selectedDevice?.instagram_version || '',
      is_restarter: dev.is_restarter || selectedDevice?.is_restarter || false,
      account_id: selectedDevice?.account_id || '',
      brand: info?.system?.brand || '',
      model: info?.system?.model || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.device_name) return;
    setEditSaving(true);
    try {
      const r = await axios.put(`/api/devices/${deviceId}`, {
        device_name: editForm.device_name,
        android_version: editForm.android_version || null,
        gmail_account: editForm.gmail_account || null,
        instagram_version: editForm.instagram_version || null,
        is_restarter: editForm.is_restarter,
        account_id: editForm.account_id || null,
      });
      if (r.data.success) { setEditOpen(false); fetchInfo(); }
      else alert('Erreur: ' + r.data.error);
    } catch (e) { alert('Erreur: ' + (e.response?.data?.error || e.message)); }
    setEditSaving(false);
  };

  const fetchInfo = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/devices/${deviceId}/system-info`);
      const d = await r.json();
      setInfo(d);
    } catch (e) {
      console.error('Failed to load device info:', e);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  // Load available APKs and check installed app versions
  const loadApps = useCallback(async () => {
    if (!deviceId) return;
    try {
      const [apkRes, clonesRes, ...versionResults] = await Promise.all([
        axios.get('/api/devices/apk/list'),
        axios.get(`/api/devices/apk/instagram-clones/${deviceId}`).catch(() => ({ data: { clones: [] } })),
        ...KNOWN_APPS.map(app =>
          axios.get(`/api/devices/apk/installed-version/${deviceId}/${app.pkg}`).catch(() => ({ data: { version: null, installed: false } }))
        )
      ]);
      setApks(apkRes.data.apks || []);
      setIgClones(clonesRes.data?.clones || []);
      const versions = {};
      KNOWN_APPS.forEach((app, i) => {
        versions[app.pkg] = versionResults[i]?.data || { version: null, installed: false };
      });
      setAppVersions(versions);
    } catch (e) { console.error('loadApps error:', e); }
  }, [deviceId]);

  const handleFreeze = async () => {
    setFreezing(true);
    try {
      await axios.post('/api/devices/freeze-playstore', { device_id: deviceId });
      alert('✅ Play Store gelé + MAJ auto désactivées');
    } catch (e) { alert('❌ Erreur: ' + (e.response?.data?.error || e.message)); }
    setFreezing(false);
  };

  const handleUnfreeze = async () => {
    setFreezing(true);
    try {
      await axios.post('/api/devices/unfreeze-playstore', { device_id: deviceId });
      alert('✅ Play Store dégelé');
    } catch (e) { alert('❌ Erreur: ' + (e.response?.data?.error || e.message)); }
    setFreezing(false);
  };

  const handleInstall = async (apkPath, apkName) => {
    setInstalling(prev => ({ ...prev, [apkName]: true }));
    try {
      const r = await axios.post('/api/devices/apk/install', { device_id: deviceId, apk_path: apkPath });
      if (r.data.success) { alert(`✅ ${apkName} installé !`); loadApps(); }
      else alert('❌ ' + (r.data.output || 'Erreur'));
    } catch (e) { alert('❌ ' + (e.response?.data?.error || e.message)); }
    setInstalling(prev => ({ ...prev, [apkName]: false }));
  };

  useEffect(() => { fetchInfo(); loadApps(); }, [fetchInfo, loadApps]);

  const toggle = (key) => setOpenCards(prev => ({ ...prev, [key]: !prev[key] }));

  if (!selectedDevice) {
    return (
      <Page>
        <NoDevice>
          <Smartphone size={48} />
          <h3>Aucun appareil sélectionné</h3>
          <p>Sélectionnez un appareil dans la barre latérale pour voir ses informations.</p>
        </NoDevice>
      </Page>
    );
  }

  const sys = info?.system || {};
  const batt = info?.battery || {};
  const scr = info?.screen || {};
  const net = info?.network || {};
  const sto = info?.storage || {};
  const cpu = info?.cpu || {};
  const mem = info?.memory || {};
  const dev = info?.device || {};

  return (
    <Page>
      <Header>
        <Title>
          <Smartphone size={20} /> Informations Appareil
          <span>{deviceId}</span>
        </Title>
        <RefreshBtn onClick={fetchInfo} disabled={loading} className={loading ? 'spinning' : ''}>
          <RefreshCw size={14} /> {loading ? 'Chargement...' : 'Actualiser'}
        </RefreshBtn>
      </Header>

      {/* Banner */}
      <DeviceBanner>
        <DeviceIcon><Smartphone size={28} /></DeviceIcon>
        <DeviceMeta>
          <h2>{selectedDevice.device_name || sys.model || 'Appareil'}</h2>
          <MetaRow>
            <MetaTag><Hash size={11} />{deviceId}</MetaTag>
            {sys.brand && <MetaTag><Layers size={11} />{sys.brand} {sys.model}</MetaTag>}
            {sys.androidVersion && <MetaTag><Server size={11} />Android {sys.androidVersion}</MetaTag>}
            {dev.instagramUsername && <MetaTag><Instagram size={11} />@{dev.instagramUsername}</MetaTag>}
            {dev.gmail_account && <MetaTag><Mail size={11} />{dev.gmail_account}</MetaTag>}
            {dev.workstation_name && <MetaTag style={{ background: 'rgba(72,219,251,.12)', color: 'var(--info)' }}><Server size={11} />{dev.workstation_name}</MetaTag>}
            <MetaTag style={{ background: dev.is_restarter ? 'rgba(232,184,74,.15)' : 'rgba(165,145,239,.1)', color: dev.is_restarter ? 'var(--warning)' : 'var(--primary)' }}>
              {dev.is_restarter ? '⟳ Restarter' : '⚙ Worker'}
            </MetaTag>
          </MetaRow>
        </DeviceMeta>
        <EditBtn onClick={openEditModal}><Pencil size={14} /> Modifier</EditBtn>
        <StatusBadge $online={info?.isOnline}>
          {info?.isOnline ? 'En ligne' : 'Hors ligne'}
        </StatusBadge>
      </DeviceBanner>

      {/* ── Edit Modal ── */}
      {editOpen && (
        <ModalOverlay onClick={() => setEditOpen(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>
              <h3><Smartphone size={20} /> Modifier le Device</h3>
              <button onClick={() => setEditOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
            </ModalTitle>
            <MGrid>
              <MField><label>Device ID</label><input value={deviceId} disabled style={{ opacity: .5 }} /></MField>
              <MField><label>Nom du device *</label><input value={editForm.device_name} onChange={e => setEditForm(f => ({ ...f, device_name: e.target.value }))} /></MField>
              <MField><label>Android Version</label><input value={editForm.android_version} onChange={e => setEditForm(f => ({ ...f, android_version: e.target.value }))} placeholder="11" /></MField>
              <MField><label>Gmail</label><input type="email" value={editForm.gmail_account} onChange={e => setEditForm(f => ({ ...f, gmail_account: e.target.value }))} placeholder="example@gmail.com" /></MField>
              <MField><label>Instagram Version</label><input value={editForm.instagram_version} onChange={e => setEditForm(f => ({ ...f, instagram_version: e.target.value }))} placeholder="367.0.0.43.89" /></MField>
              <MField><label>Marque</label><input value={editForm.brand} disabled style={{ opacity: .5 }} /></MField>
              <MField><label>Modèle</label><input value={editForm.model} disabled style={{ opacity: .5 }} /></MField>
              <MField className="wide">
                <label>Compte Instagram</label>
                <select value={editForm.account_id || ''} onChange={e => setEditForm(f => ({ ...f, account_id: e.target.value }))}>
                  <option value="">Aucun compte</option>
                  {allAccounts.map(a => (
                    <option key={a.id} value={a.id}>@{a.username || a.instagramUsername || `ID ${a.id}`}</option>
                  ))}
                </select>
              </MField>
              <MField className="wide">
                <label>Rôle du device</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  <RoleToggle $active={!editForm.is_restarter} onClick={() => setEditForm(f => ({ ...f, is_restarter: false }))}>
                    <Smartphone size={14} /> Worker
                  </RoleToggle>
                  <RoleToggle $active={editForm.is_restarter} $warn onClick={() => setEditForm(f => ({ ...f, is_restarter: true }))}>
                    <RotateCcw size={14} /> Restarter
                  </RoleToggle>
                </div>
              </MField>
            </MGrid>
            <MActions>
              <MBtn $primary onClick={saveEdit} disabled={editSaving}>
                <Save size={14} /> {editSaving ? 'Sauvegarde...' : 'Enregistrer'}
              </MBtn>
              <MBtn onClick={() => setEditOpen(false)}>Annuler</MBtn>
            </MActions>
          </ModalCard>
        </ModalOverlay>
      )}

      <CardsGrid>
        {/* ── Système ── */}
        <Card>
          <CardHead onClick={() => toggle('system')}>
            <Server size={18} /> Système
            {openCards.system ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.system}>
            <CardContent>
              <InfoRow><InfoLabel>Marque</InfoLabel><InfoValue>{sys.brand || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Modèle</InfoLabel><InfoValue>{sys.model || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Android</InfoLabel><InfoValue>{sys.androidVersion || 'N/A'} (SDK {sys.sdkLevel || '?'})</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Build</InfoLabel><InfoValue $mono>{sys.buildId || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Serial</InfoLabel><InfoValue $mono>{sys.serial || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel><Clock size={12} /> Uptime</InfoLabel><InfoValue>{sys.uptime || 'N/A'}</InfoValue></InfoRow>
              {dev.workflow && <InfoRow><InfoLabel>Workflow</InfoLabel><InfoValue>{dev.workflow}</InfoValue></InfoRow>}
            </CardContent>
          </CardBody>
        </Card>

        {/* ── Batterie ── */}
        <Card>
          <CardHead onClick={() => toggle('battery')}>
            <Battery size={18} /> Batterie
            {openCards.battery ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.battery}>
            <CardContent>
              <InfoRow>
                <InfoLabel><Zap size={12} /> Niveau</InfoLabel>
                <InfoValue>{batt.level || 0}%</InfoValue>
              </InfoRow>
              <ProgressBar><ProgressFill $pct={batt.level || 0} /></ProgressBar>
              <InfoRow><InfoLabel>Statut</InfoLabel><InfoValue>{batt.status || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel><Thermometer size={12} /> Température</InfoLabel><InfoValue>{batt.temperature || 0}°C</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Voltage</InfoLabel><InfoValue>{batt.voltage || 0} V</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Santé</InfoLabel><InfoValue>{batt.health || 'N/A'}</InfoValue></InfoRow>
            </CardContent>
          </CardBody>
        </Card>

        {/* ── Écran ── */}
        <Card>
          <CardHead onClick={() => toggle('screen')}>
            <Monitor size={18} /> Écran
            {openCards.screen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.screen}>
            <CardContent>
              <InfoRow><InfoLabel>Résolution</InfoLabel><InfoValue>{scr.resolution || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Densité</InfoLabel><InfoValue>{scr.density || 'N/A'}</InfoValue></InfoRow>
            </CardContent>
          </CardBody>
        </Card>

        {/* ── Réseau ── */}
        <Card>
          <CardHead onClick={() => toggle('network')}>
            <Wifi size={18} /> Réseau
            {openCards.network ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.network}>
            <CardContent>
              <InfoRow><InfoLabel><Globe size={12} /> IP</InfoLabel><InfoValue $mono>{net.ip || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel><Signal size={12} /> SSID</InfoLabel><InfoValue>{net.ssid || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>MAC</InfoLabel><InfoValue $mono>{net.mac || 'N/A'}</InfoValue></InfoRow>
            </CardContent>
          </CardBody>
        </Card>

        {/* ── Stockage ── */}
        <Card>
          <CardHead onClick={() => toggle('storage')}>
            <HardDrive size={18} /> Stockage
            {openCards.storage ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.storage}>
            <CardContent>
              <InfoRow>
                <InfoLabel>Utilisé</InfoLabel>
                <InfoValue>{fmtKB(sto.usedKB)} / {fmtKB(sto.totalKB)}</InfoValue>
              </InfoRow>
              <ProgressBar><ProgressFill $pct={sto.percent || 0} /></ProgressBar>
              <InfoRow><InfoLabel>Libre</InfoLabel><InfoValue>{fmtKB(sto.freeKB)}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Utilisation</InfoLabel><InfoValue>{sto.percent || 0}%</InfoValue></InfoRow>
            </CardContent>
          </CardBody>
        </Card>

        {/* ── CPU & Mémoire ── */}
        <Card>
          <CardHead onClick={() => toggle('cpu')}>
            <Cpu size={18} /> CPU & Mémoire
            {openCards.cpu ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.cpu}>
            <CardContent>
              <InfoRow><InfoLabel>Cœurs CPU</InfoLabel><InfoValue>{cpu.cores || 'N/A'}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Architecture</InfoLabel><InfoValue>{cpu.abi || 'N/A'}</InfoValue></InfoRow>
              <InfoRow>
                <InfoLabel><Activity size={12} /> RAM utilisée</InfoLabel>
                <InfoValue>{fmtKB(mem.usedKB)} / {fmtKB(mem.totalKB)}</InfoValue>
              </InfoRow>
              <ProgressBar><ProgressFill $pct={mem.percent || 0} /></ProgressBar>
              <InfoRow><InfoLabel>RAM disponible</InfoLabel><InfoValue>{fmtKB(mem.availableKB)}</InfoValue></InfoRow>
              <InfoRow><InfoLabel>Utilisation RAM</InfoLabel><InfoValue>{mem.percent || 0}%</InfoValue></InfoRow>
            </CardContent>
          </CardBody>
        </Card>
        {/* ── Apps & Play Store ── */}
        <Card style={{ gridColumn: '1 / -1' }}>
          <CardHead onClick={() => toggle('apps')}>
            <Package size={18} /> Apps & Play Store
            {openCards.apps ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </CardHead>
          <CardBody $open={openCards.apps} style={{ maxHeight: openCards.apps ? '2000px' : '0' }}>
            <CardContent>
              {/* Play Store controls */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                <ActionBtn $danger onClick={handleFreeze} disabled={freezing || !info?.isOnline}>
                  <ShieldOff size={13} /> {freezing ? 'En cours...' : 'Geler Play Store + MAJ auto'}
                </ActionBtn>
                <ActionBtn $success onClick={handleUnfreeze} disabled={freezing || !info?.isOnline}>
                  <ShieldCheck size={13} /> Dégeler Play Store
                </ActionBtn>
              </div>

              {/* ── Render each KNOWN_APP as its own section ── */}
              {KNOWN_APPS.map(app => {
                const IconComponent = APP_ICONS[app.icon];
                const ver = appVersions[app.pkg];
                // Find matching APKs from the folder for this app
                const matchingApks = apks.filter(a => {
                  const fn = a.filename.toLowerCase();
                  // For Instagram original, match "instagram" but exclude clone packages like "androie", "androif" etc.
                  if (app.pkg === 'com.instagram.android') {
                    return (fn.includes('instagram') && !fn.match(/com\.instagram\.andro[^i]/)) || fn.startsWith('com.instagram.android');
                  }
                  return app.apkKeywords.some(kw => fn.includes(kw.toLowerCase()));
                });
                const selectedApk = selectedApkByPkg[app.pkg];
                const apkToInstall = selectedApk ? apks.find(a => a.filename === selectedApk) : (matchingApks.length > 0 ? matchingApks[0] : null);
                // Get clones for Instagram
                const clones = app.pkg === 'com.instagram.android' ? igClones.filter(c => !c.isOriginal) : [];

                return (
                  <div key={app.pkg} style={{ marginBottom: 20 }}>
                    {/* Section header */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: app.color, marginBottom: 8, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {IconComponent && IconComponent({ size: 13 }, isDark)} {app.name}
                    </div>

                    {/* Main app card */}
                    <ApkItem style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: app.icon === 'instagram' ? 'linear-gradient(135deg, #833AB4, #E1306C, #F77737)' : app.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {IconComponent && IconComponent({ size: 18, style: { color: '#fff' } }, isDark)}
                          </div>
                          <div>
                            <ApkName>{app.name} <span>{app.pkg}</span></ApkName>
                            <StatusDot $ok={ver?.installed}>
                              {ver?.installed
                                ? <><CheckCircle size={10} /> Installé: <strong style={{ color: 'var(--text-primary)' }}>v{ver.version}</strong></>
                                : <><XCircle size={10} /> Non installé</>}
                            </StatusDot>
                          </div>
                        </div>
                        {app.mirror && (
                          <MirrorLink onClick={() => openExternal(app.mirror)}>
                            <ExternalLink size={10} /> APK Mirror
                          </MirrorLink>
                        )}
                      </div>

                      {/* APK dropdown + install button */}
                      {matchingApks.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>APK :</span>
                          <ApkVersionSelect
                            value={selectedApkByPkg[app.pkg] || matchingApks[0]?.filename || ''}
                            onChange={e => setSelectedApkByPkg(prev => ({ ...prev, [app.pkg]: e.target.value }))}
                          >
                            {matchingApks.map(a => (
                              <option key={a.filename} value={a.filename}>{a.name} ({a.sizeMB} MB)</option>
                            ))}
                          </ApkVersionSelect>
                          <ActionBtn
                            onClick={() => apkToInstall && handleInstall(apkToInstall.path, apkToInstall.name)}
                            disabled={!apkToInstall || installing[apkToInstall?.name] || !info?.isOnline}
                            style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                          >
                            {installing[apkToInstall?.name]
                              ? <><Loader size={12} className="spin" /> Installation...</>
                              : <><Download size={12} /> {ver?.installed ? 'Réinstaller' : 'Installer'} {apkToInstall?.name?.match(/[\d.]+/) ? `v${apkToInstall.name.match(/[\d.]+/)[0]}` : ''}</>}
                          </ActionBtn>
                        </div>
                      ) : (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 0', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                          Aucun APK correspondant dans le dossier <code>apks/</code>.
                          {app.mirror && <>{' '}<MirrorLink onClick={() => openExternal(app.mirror)} style={{ fontSize: 10 }}><Download size={10} /> Télécharger</MirrorLink></>}
                        </div>
                      )}
                    </ApkItem>

                    {/* ── Instagram Clones (sub-list) ── */}
                    {clones.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#E1306C', marginBottom: 6, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5, paddingLeft: 4 }}>
                          <Instagram size={11} /> Clones ({clones.length})
                        </div>
                        <div style={{ maxHeight: 500, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
                          {clones.map(clone => {
                            const cloneLetter = (clone.suffix || clone.package.replace('com.instagram.android', '')).toUpperCase();
                            const cloneApks = apks.filter(a =>
                              a.filename.toLowerCase().includes(clone.package.replace('com.instagram.', '').toLowerCase())
                            );
                            const selCloneApk = selectedApkByPkg[clone.package];
                            const cloneApkToInstall = selCloneApk ? apks.find(a => a.filename === selCloneApk) : (cloneApks.length > 0 ? cloneApks[0] : null);
                            return (
                              <ApkItem key={clone.package} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #833AB4, #E1306C, #F77737)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                                      <Instagram size={16} style={{ color: '#fff' }} />
                                      <span style={{ position: 'absolute', bottom: -2, right: -2, background: 'var(--primary)', color: '#fff', fontSize: 8, fontWeight: 900, borderRadius: 3, padding: '1px 3px', lineHeight: 1.2 }}>{cloneLetter}</span>
                                    </div>
                                    <div>
                                      <ApkName>
                                        Clone I<strong style={{ color: '#E1306C', fontSize: 14 }}>{cloneLetter}</strong>
                                        {' '}<span>{clone.package}</span>
                                      </ApkName>
                                      <StatusDot $ok={clone.installed}>
                                        {clone.installed
                                          ? <><CheckCircle size={10} /> v{clone.version || '?'}</>
                                          : <><XCircle size={10} /> Non installé</>}
                                      </StatusDot>
                                    </div>
                                  </div>
                                </div>
                                {cloneApks.length > 0 ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>APK :</span>
                                    <ApkVersionSelect
                                      value={selectedApkByPkg[clone.package] || cloneApks[0]?.filename || ''}
                                      onChange={e => setSelectedApkByPkg(prev => ({ ...prev, [clone.package]: e.target.value }))}
                                    >
                                      {cloneApks.map(a => (
                                        <option key={a.filename} value={a.filename}>{a.name} ({a.sizeMB} MB)</option>
                                      ))}
                                    </ApkVersionSelect>
                                    <ActionBtn
                                      onClick={() => cloneApkToInstall && handleInstall(cloneApkToInstall.path, cloneApkToInstall.name)}
                                      disabled={!cloneApkToInstall || installing[cloneApkToInstall?.name] || !info?.isOnline}
                                      style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                                    >
                                      {installing[cloneApkToInstall?.name]
                                        ? <><Loader size={12} className="spin" /> Installation...</>
                                        : <><Download size={12} /> {clone.installed ? 'Mettre à jour' : 'Installer'}</>}
                                    </ActionBtn>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 0' }}>
                                    Aucun APK pour « <code>{clone.package.replace('com.instagram.', '')}</code> » dans <code>apks/</code>.
                                  </div>
                                )}
                              </ApkItem>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── All APKs from folder ── */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginBottom: 10, marginTop: 20, textTransform: 'uppercase', letterSpacing: .5, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Package size={13} /> Tous les APK du dossier ({apks.length})
              </div>
              {apks.length === 0 ? (
                <div style={{ padding: '16px 12px', background: 'var(--surface)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                  Aucun APK trouvé. Placez vos fichiers <code>.apk</code> dans le dossier <code>apks/</code>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {apks.map(apk => (
                    <ApkItem key={apk.filename} style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 18 }}>📦</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ApkName style={{ fontSize: 12 }}>{apk.name}</ApkName>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {apk.filename} — {apk.sizeMB} MB
                          </div>
                        </div>
                      </div>
                      <ActionBtn
                        onClick={() => handleInstall(apk.path, apk.name)}
                        disabled={installing[apk.name] || !info?.isOnline}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {installing[apk.name]
                          ? <><Loader size={12} className="spin" /> Installation...</>
                          : <><Download size={12} /> Installer</>}
                      </ActionBtn>
                    </ApkItem>
                  ))}
                </div>
              )}
            </CardContent>
          </CardBody>
        </Card>
      </CardsGrid>
    </Page>
  );
};

export default DeviceInfo;
